// imported from myintelli-onnx commit 9f78984 on 2026-05-04
// do not edit manually — regenerate via `node scripts/sync-onnx-vendor.mjs`
import type { PipelineConfig } from './config.js';
import type { ImageFrame, PreprocessingMetrics } from './types.js';

/**
 * CLAHE + adaptive gamma in pure JS. Designed to match the Python implementation
 * close enough for parity tests but deliberately simpler — full OpenCV CLAHE
 * is ~200 LOC and we don't need the corner-case fidelity here.
 *
 * For production hot paths (mobile recognition), the platform adapter SHOULD
 * delegate to a native implementation (OpenCV via JNI on Android, Accelerate
 * on iOS) and call this only as a fallback.
 */
export class Preprocessor {
  private readonly gammaLut = new Map<number, Uint8Array>();

  constructor(private readonly cfg: PipelineConfig) {}

  meanLuminance(frame: ImageFrame): number {
    return computeMeanLuminance(frame);
  }

  apply(frame: ImageFrame): { frame: ImageFrame; metrics: PreprocessingMetrics } {
    const lumIn = computeMeanLuminance(frame);
    let out = frame;
    let gammaApplied = 1.0;

    if (this.cfg.applyGamma) {
      if (lumIn < this.cfg.gammaLowLumThreshold) {
        gammaApplied = 0.6;
      } else if (lumIn > this.cfg.gammaHighLumThreshold) {
        gammaApplied = 1.4;
      }
      if (gammaApplied !== 1.0) {
        out = applyGammaToFrame(out, gammaApplied, this.gammaLut);
      }
    }

    if (this.cfg.applyClahe) {
      out = applyClaheY(out, this.cfg.claheClipLimit, this.cfg.claheTileGrid);
    }

    return {
      frame: out,
      metrics: {
        meanLuminanceIn: lumIn,
        meanLuminanceOut: computeMeanLuminance(out),
        gammaApplied,
      },
    };
  }
}

function computeMeanLuminance(frame: ImageFrame): number {
  const stride = frame.format === 'rgba' ? 4 : 3;
  let sum = 0;
  let n = 0;
  for (let i = 0; i < frame.data.length; i += stride) {
    let r: number;
    let g: number;
    let b: number;
    if (frame.format === 'bgr') {
      b = frame.data[i] ?? 0;
      g = frame.data[i + 1] ?? 0;
      r = frame.data[i + 2] ?? 0;
    } else {
      r = frame.data[i] ?? 0;
      g = frame.data[i + 1] ?? 0;
      b = frame.data[i + 2] ?? 0;
    }
    // Y' from BT.601: 0.299 R + 0.587 G + 0.114 B
    sum += 0.299 * r + 0.587 * g + 0.114 * b;
    n++;
  }
  return n ? sum / n : 0;
}

function applyGammaToFrame(
  frame: ImageFrame,
  gamma: number,
  cache: Map<number, Uint8Array>,
): ImageFrame {
  let lut = cache.get(gamma);
  if (!lut) {
    lut = new Uint8Array(256);
    for (let i = 0; i < 256; i++) {
      lut[i] = Math.min(255, Math.max(0, Math.round(((i / 255) ** gamma) * 255)));
    }
    cache.set(gamma, lut);
  }
  const out = new Uint8Array(frame.data.length);
  const stride = frame.format === 'rgba' ? 4 : 3;
  for (let i = 0; i < frame.data.length; i += stride) {
    out[i] = lut[frame.data[i] ?? 0]!;
    out[i + 1] = lut[frame.data[i + 1] ?? 0]!;
    out[i + 2] = lut[frame.data[i + 2] ?? 0]!;
    if (frame.format === 'rgba') out[i + 3] = frame.data[i + 3] ?? 255;
  }
  return { ...frame, data: out };
}

/**
 * Simplified CLAHE on the luma channel. Tile-based histogram equalization with
 * a clip limit, then bilinear interpolation between tile lookup tables for
 * pixel mapping. Matches OpenCV behavior to within a few luminance counts —
 * good enough for the gate decisions; not bit-identical.
 */
function applyClaheY(
  frame: ImageFrame,
  clipLimit: number,
  tileGrid: readonly [number, number],
): ImageFrame {
  const w = frame.width;
  const h = frame.height;
  const stride = frame.format === 'rgba' ? 4 : 3;
  const [tilesY, tilesX] = tileGrid;
  const tileH = Math.floor(h / tilesY);
  const tileW = Math.floor(w / tilesX);
  if (tileH === 0 || tileW === 0) return frame; // image smaller than grid; no-op

  // Build per-tile lookup tables in luma space
  const luts: Uint8Array[] = [];
  for (let ty = 0; ty < tilesY; ty++) {
    for (let tx = 0; tx < tilesX; tx++) {
      luts.push(buildTileLut(frame, tx * tileW, ty * tileH, tileW, tileH, stride, clipLimit));
    }
  }

  const out = new Uint8Array(frame.data.length);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * stride;
      const r =
        frame.format === 'bgr' ? (frame.data[i + 2] ?? 0) : (frame.data[i] ?? 0);
      const g = frame.data[i + 1] ?? 0;
      const b =
        frame.format === 'bgr' ? (frame.data[i] ?? 0) : (frame.data[i + 2] ?? 0);

      const yLuma = Math.min(
        255,
        Math.max(0, Math.round(0.299 * r + 0.587 * g + 0.114 * b)),
      );

      // Find the 4 surrounding tile centers and interpolate the LUT outputs
      const newY = bilinearLut(luts, tilesX, tilesY, tileW, tileH, x, y, yLuma);

      // Apply scaling to RGB to preserve color (multiply each channel by newY/oldY)
      const scale = yLuma > 0 ? newY / yLuma : 1;
      const rOut = Math.min(255, Math.round(r * scale));
      const gOut = Math.min(255, Math.round(g * scale));
      const bOut = Math.min(255, Math.round(b * scale));

      if (frame.format === 'bgr') {
        out[i] = bOut;
        out[i + 1] = gOut;
        out[i + 2] = rOut;
      } else {
        out[i] = rOut;
        out[i + 1] = gOut;
        out[i + 2] = bOut;
      }
      if (frame.format === 'rgba') out[i + 3] = frame.data[i + 3] ?? 255;
    }
  }
  return { ...frame, data: out };
}

function buildTileLut(
  frame: ImageFrame,
  tx: number,
  ty: number,
  tw: number,
  th: number,
  stride: number,
  clipLimit: number,
): Uint8Array {
  const hist = new Uint32Array(256);
  for (let y = 0; y < th; y++) {
    for (let x = 0; x < tw; x++) {
      const i = ((ty + y) * frame.width + (tx + x)) * stride;
      const r =
        frame.format === 'bgr' ? (frame.data[i + 2] ?? 0) : (frame.data[i] ?? 0);
      const g = frame.data[i + 1] ?? 0;
      const b =
        frame.format === 'bgr' ? (frame.data[i] ?? 0) : (frame.data[i + 2] ?? 0);
      const lum = Math.min(
        255,
        Math.max(0, Math.round(0.299 * r + 0.587 * g + 0.114 * b)),
      );
      hist[lum]!++;
    }
  }

  // Clip the histogram and redistribute the excess
  const totalPixels = tw * th;
  const clip = Math.max(1, Math.floor((clipLimit * totalPixels) / 256));
  let excess = 0;
  for (let i = 0; i < 256; i++) {
    if (hist[i]! > clip) {
      excess += hist[i]! - clip;
      hist[i] = clip;
    }
  }
  const redist = Math.floor(excess / 256);
  for (let i = 0; i < 256; i++) hist[i]! += redist;

  // Cumulative distribution → LUT
  const lut = new Uint8Array(256);
  let cum = 0;
  for (let i = 0; i < 256; i++) {
    cum += hist[i]!;
    lut[i] = Math.min(255, Math.round((cum * 255) / totalPixels));
  }
  return lut;
}

function bilinearLut(
  luts: Uint8Array[],
  tilesX: number,
  tilesY: number,
  tileW: number,
  tileH: number,
  x: number,
  y: number,
  v: number,
): number {
  // Tile centers are at (tx*tileW + tileW/2, ty*tileH + tileH/2)
  const fx = x / tileW - 0.5;
  const fy = y / tileH - 0.5;
  const tx0 = Math.max(0, Math.min(tilesX - 1, Math.floor(fx)));
  const ty0 = Math.max(0, Math.min(tilesY - 1, Math.floor(fy)));
  const tx1 = Math.min(tilesX - 1, tx0 + 1);
  const ty1 = Math.min(tilesY - 1, ty0 + 1);
  const dx = Math.max(0, Math.min(1, fx - tx0));
  const dy = Math.max(0, Math.min(1, fy - ty0));

  const q00 = luts[ty0 * tilesX + tx0]![v]!;
  const q10 = luts[ty0 * tilesX + tx1]![v]!;
  const q01 = luts[ty1 * tilesX + tx0]![v]!;
  const q11 = luts[ty1 * tilesX + tx1]![v]!;

  return Math.round(
    q00 * (1 - dx) * (1 - dy) +
      q10 * dx * (1 - dy) +
      q01 * (1 - dx) * dy +
      q11 * dx * dy,
  );
}
